import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthStore, LoginCredentials, ChangePasswordRequest, ResetPasswordRequest, ResetPasswordConfirm } from '../types/auth';
import { getSecureToken, clearSecureToken } from '../utils/secureStorage'; // 从安全存储读取/清理 token
import { jwtDecode } from 'jwt-decode'; // 解析 JWT 以做校验与角色映射

// 模拟API调用 - 在实际项目中这些会调用真实的API
const mockAuthAPI = {
  login: async (credentials: LoginCredentials) => {
    // 模拟网络延迟
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 简单的模拟验证
    // 超级管理员演示账号（统一更新）：support@ntsports.tech / holo#Motion
    if (credentials.email === 'support@ntsports.tech' && credentials.password === 'holo#Motion') {
      return {
        user: {
          id: '1',
          email: 'support@ntsports.tech',
          name: 'Super Admin',
          role: 'super_admin' as const,
          avatar: undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        token: 'mock-jwt-token',
        refreshToken: 'mock-refresh-token',
        expiresIn: 3600
      };
    } else if (credentials.email === 'org@example.com' && credentials.password === 'org123') {
      return {
        user: {
          id: '2',
          email: 'org@example.com',
          name: 'Admin User',
          role: 'admin' as const,
          avatar: undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        token: 'mock-jwt-token-org',
        refreshToken: 'mock-refresh-token-org',
        expiresIn: 3600
      };
    }
    
    throw new Error('Invalid credentials');
  },

  refreshToken: async (_refreshToken: string) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      token: 'new-mock-jwt-token',
      refreshToken: 'new-mock-refresh-token',
      expiresIn: 3600
    };
  },

  validateToken: async (token: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return token.startsWith('mock-jwt-token');
  }
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      user: null,
      isAuthenticated: false,
      userRole: null,
      token: null,
      refreshToken: null,
      profile: null,
      loading: false,
      error: null,

      // 登录
      login: async (credentials: LoginCredentials) => {
        set({ loading: true, error: null });
        
        try {
          const response = await mockAuthAPI.login(credentials);
          
          set({
            user: response.user,
            isAuthenticated: true,
            userRole: response.user.role,
            token: response.token,
            refreshToken: response.refreshToken,
            loading: false,
            error: null
          });
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            userRole: null,
            token: null,
            refreshToken: null,
            loading: false,
            error: error instanceof Error ? error.message : 'Login failed'
          });
          throw error;
        }
      },

      // 登出
      logout: () => {
        // 同步清理安全存储中的 token，避免残留导致误判已登录
        clearSecureToken();
        // 强制清理持久化存储，防止状态残留
        try {
          localStorage.removeItem('auth-storage');
          // 尝试调用 zustand persist middleware 的清理方法
          (useAuthStore as any).persist?.clearStorage?.();
        } catch (e) {
          console.error('Failed to clear auth storage:', e);
        }
        
        set({
          user: null,
          isAuthenticated: false,
          userRole: null,
          token: null,
          refreshToken: null,
          profile: null,
          loading: false,
          error: null
        });
      },

      // 刷新令牌
      refreshAuthToken: async () => {
        const { refreshToken: currentRefreshToken } = get();
        
        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        set({ loading: true });
        
        try {
          const response = await mockAuthAPI.refreshToken(currentRefreshToken);
          
          set({
            token: response.token,
            refreshToken: response.refreshToken,
            loading: false
          });
        } catch (error) {
          // 刷新失败，清除认证状态
          set({
            user: null,
            isAuthenticated: false,
            userRole: null,
            token: null,
            refreshToken: null,
            loading: false,
            error: 'Session expired'
          });
          throw error;
        }
      },

      // 验证令牌（增强版）：优先验证真实 JWT，其次兼容原有 mock 逻辑
      validateToken: async () => {
        // 1) 优先从安全存储中读取真实 token 并校验
        const securedToken = getSecureToken();
        if (securedToken) {
          try {
            const payload: any = jwtDecode(securedToken);
            // 校验过期时间（若存在），exp 单位为秒
            const exp = payload?.exp;
            if (typeof exp === 'number' && exp * 1000 < Date.now()) {
              // 过期：清除安全存储与 store 状态
              clearSecureToken();
              set({
                user: null,
                isAuthenticated: false,
                userRole: null,
                token: null,
                refreshToken: null
              });
              return false;
            }

            // 映射 JWT scope 到内部角色
            const scope = payload?.scope;
            const role = scope === 'platform_admin'
              ? ('super_admin' as const)
              : scope === 'org_admin'
              ? ('admin' as const)
              : (scope === 'team_member' || scope === 'personal')
              ? ('member' as const)
              : ('admin' as const);
            const email = payload?.email ?? 'support@ntsports.tech';
            const sub = payload?.sub ?? 'jwt-user';

            // 填充最小用户信息，确保受保护路由正常放行
            set({
              user: {
                id: String(sub),
                email,
                name: 'Authenticated User',
                role,
                avatar: undefined,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              },
              isAuthenticated: true,
              userRole: role,
              token: null, // 避免明文持久化
              refreshToken: null
            });
            return true;
          } catch (e) {
            // 解析失败视为无效
            clearSecureToken();
            set({
              user: null,
              isAuthenticated: false,
              userRole: null,
              token: null,
              refreshToken: null
            });
            return false;
          }
        }

        // 2) 回退：保持原有 mock token 验证逻辑
        const { token } = get();
        if (!token) {
          return false;
        }
        try {
          const isValid = await mockAuthAPI.validateToken(token);
          if (!isValid) {
            set({
              user: null,
              isAuthenticated: false,
              userRole: null,
              token: null,
              refreshToken: null
            });
          }
          return isValid;
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            userRole: null,
            token: null,
            refreshToken: null
          });
          return false;
        }
      },

      // 修改密码
      changePassword: async (_request: ChangePasswordRequest) => {
        set({ loading: true, error: null });
        
        try {
          // 模拟API调用
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // 在实际应用中，这里会调用真实的API
          console.log('Password changed successfully');
          
          set({ loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to change password'
          });
          throw error;
        }
      },

      // 请求密码重置
      requestPasswordReset: async (_request: ResetPasswordRequest) => {
        set({ loading: true, error: null });
        
        try {
          // 模拟API调用
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          console.log('Password reset email sent');
          
          set({ loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to send reset email'
          });
          throw error;
        }
      },

      // 重置密码
      resetPassword: async (_request: ResetPasswordConfirm) => {
        set({ loading: true, error: null });
        
        try {
          // 模拟API调用
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          console.log('Password reset successfully');
          
          set({ loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to reset password'
          });
          throw error;
        }
      },

      // 清除错误
      clearError: () => {
        set({ error: null });
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // 只持久化必要的认证信息
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        userRole: state.userRole,
        token: state.token,
        refreshToken: state.refreshToken,
        profile: state.profile
      })
    }
  )
);
